/**
 * Вариант посевного плана — назначение техкарт (этап 2(в) планирования-2027).
 *
 * Макет (утверждён founder 18.08):
 *   `docs/superpowers/prototypes/annual-planning/v2-04-sowing-plan.html`
 *   (ветка `docs/wireframes-season-planning-v2`, скриншот рядом).
 *
 * Что экран обещает пользователю:
 *   • структура посевов уже разложена по полям — здесь агроном говорит, ПО
 *     КАКОЙ ТЕХНОЛОГИИ каждая культура будет расти: на строку «культура по
 *     предшественнику» назначается вариант техкарты;
 *   • пока вариант не назначен — экономика по этой площади не считается, и
 *     это видно в покрытии, а не прячется за нулём;
 *   • второй вариант плана собирается копированием назначений соседнего и
 *     правкой одной-двух строк — это и есть «2-4 варианта за час».
 *
 * Чего здесь СОЗНАТЕЛЬНО нет:
 *   • «оставить старый расчёт» и история расчётов — требуют хранения снимка
 *     расчёта (следующий PR, миграция);
 *   • техкарты ухода за паром — карта заводится на культуру
 *     (`tech_cards.crop_id` NOT NULL), пар показан строкой без техкарты;
 *   • замечаний советчика — движка правил ещё нет, рисовать пустой блок,
 *     который никогда ничего не скажет, — обман.
 */
import { useMemo } from 'react';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Skeleton } from '@/components/ui/skeleton';
import { useCan } from '@/lib/auth/usePermissions';
import { useSeasonByYear } from '@/lib/planning/useSeasonByYear';
import {
  formatDate,
  formatHa,
  formatMln,
  formatRubPerHa,
  formatTons,
  groupRowsByCrop,
  useCopyAssignments,
  useVariantSummary,
  type PlanRow,
} from '@/lib/planning/planVariant';
import {
  useAdoptTechCard,
  useAssignTechCard,
  useCloneTechCard,
  usePatchTechCard,
  usePlanVariants,
  useTechCardCandidates,
  useUnassignTechCard,
} from '@/lib/planning/techCards';
import { describeApiError } from '@/lib/apiError';
import { toast } from '@/lib/toast';
import { FarmScopeNotice } from '@/components/scope/FarmScopeNotice';
import { usePlanningFarmScope } from '@/components/scope/usePlanningFarmScope';
import { CreateVariantButton } from '../CreateVariantButton';
import { SeasonNotStartedCard, SeasonYearOutOfWindowCard } from '../SeasonBudgetStates';
import { AddCropButton } from './AddCropDialog';
import { AssignPanel, type AssignAction } from './AssignPanel';
import { RemoveCropButton } from './RemoveCropButton';
import { CoverageCard } from './CoverageCard';
import { PlanRowsTable } from './PlanRowsTable';
import { VariantTabs } from './VariantTabs';

export function PlanVariantPage() {
  const params = useParams({ strict: false }) as { year?: string };
  const year = Number(params.year);
  const search = useSearch({ strict: false }) as { farm?: string; variant?: string; row?: string };
  const navigate = useNavigate();
  const season = useSeasonByYear(year);

  const canRead = useCan('tech_cards', 'r');
  const canWrite = useCan('tech_cards', 'u');
  const canCreate = useCan('tech_cards', 'c');

  // Хозяйство — из переключателя слева (AK-866 В-3: область — из
  // переключателя); `?farm=` — только присланная ссылка (AK-897).
  const farmScope = usePlanningFarmScope({
    searchFarm: search.farm,
    onScopeChanged: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant',
        params: { year: String(year) },
        search: { farm: undefined, variant: undefined, row: undefined },
        replace: true,
      }),
    onDeepLinkApplied: () =>
      void navigate({
        to: '/planning/seasons/$year/plan-variant',
        params: { year: String(year) },
        search: { ...search, farm: undefined },
        replace: true,
      }),
  });
  const { farmId } = farmScope;
  // Имя хозяйства для диалога создания варианта — из того же списка областей,
  // что показывает переключатель: два источника имён разъезжались бы.
  const farmName = farmScope.farm?.name ?? null;
  const variants = usePlanVariants(canRead ? farmId : null, season.seasonId);
  // Вариант из адреса, иначе «План сезона» (is_selected), иначе первый.
  const variantId =
    search.variant ??
    variants.data?.find((v) => v.is_selected)?.id ??
    variants.data?.[0]?.id ??
    null;

  const summary = useVariantSummary(canRead ? variantId : null);
  const rows = summary.data?.rows ?? [];
  const selectedRow =
    rows.find((r) => r.key === search.row) ??
    rows.find((r) => r.state === 'unassigned') ??
    rows.find((r) => r.state === 'assigned') ??
    null;

  // Подбор считает сервер (AK-770): он знает про карты уровня компании, про
  // карты соседних хозяйств (их берут копией) и про «ту же культуру,
  // записанную иначе». Прежний клиентский фильтр `crop_id === row.crop_id` по
  // списку карт сезона показывал 0 вариантов там, где карты есть, и предлагал
  // те, что назначить нельзя.
  const cards = useTechCardCandidates(canRead ? variantId : null, selectedRow?.crop_id ?? null);
  const cropCards = useMemo(() => cards.data ?? [], [cards.data]);
  // Имена предшественников для подписи вариантов берём из самих строк плана:
  // бэк уже отдал их резолвленными, второй справочный запрос не нужен.
  const predecessorNames = useMemo(() => {
    const names: Record<string, string> = {};
    for (const row of rows) {
      if (row.predecessor_crop_id && row.predecessor_name) names[row.predecessor_crop_id] = row.predecessor_name;
      if (row.tech_card?.predecessor_crop_id && row.tech_card.predecessor_name) {
        names[row.tech_card.predecessor_crop_id] = row.tech_card.predecessor_name;
      }
    }
    return names;
  }, [rows]);

  const assign = useAssignTechCard();
  const adopt = useAdoptTechCard();
  const unassign = useUnassignTechCard();
  const clone = useCloneTechCard();
  const patchCard = usePatchTechCard();
  const copyAssignments = useCopyAssignments();
  const busy =
    assign.isPending ||
    adopt.isPending ||
    unassign.isPending ||
    clone.isPending ||
    patchCard.isPending ||
    copyAssignments.isPending;

  // Адрес переписываем явным путём роута, а не относительным `.`: относительная
  // навигация в TanStack Router резолвится от `from`, которого у нас нет
  // (страница читает params нестрого) — соседние экраны раздела делают так же.
  const setSearch = (patch: Record<string, string | undefined>) => {
    void navigate({
      to: '/planning/seasons/$year/plan-variant',
      params: { year: String(year) },
      search: { farm: search.farm, variant: variantId ?? undefined, row: search.row, ...patch },
    });
  };

  /**
   * Назначение строке. Вариант, заведённый под ДРУГОЙ предшественник, нельзя
   * просто повесить на строку — он покрывает свои строки. Поэтому для него
   * делаем копию под предшественник строки и назначаем уже её: это честное
   * «сделать новый вариант под этот предшественник» из макета.
   */
  const handleAssign = async ({ cardId, row, needsFork, adoptRequired }: AssignAction) => {
    if (!variantId || !row.crop_id) return;
    let targetCardId = cardId;
    try {
      // Карта соседнего хозяйства (AK-770): берём копию в своё хозяйство —
      // сервер делает копию и назначение одним запросом и не плодит копии при
      // повторном клике. Правки такой карты не заденут чужой план.
      if (adoptRequired) {
        const taken = await adopt.mutateAsync({
          scenarioId: variantId,
          techCardId: cardId,
          // Культура СТРОКИ, а не культура карты: подбор предлагает и «ту же
          // культуру, записанную иначе» — без этого копия вставала на культуру
          // карты и строка оставалась непокрытой при зелёном тосте.
          cropId: row.crop_id,
          areaHa: row.area_ha,
          targetYieldCHa: row.yield_c_ha,
        });
        if (needsFork) {
          await patchCard.mutateAsync({
            cardId: taken.tech_card_id,
            patch: { predecessor_crop_id: row.predecessor_crop_id },
          });
        }
        toast.success(taken.adopted ? 'Карта взята в ваше хозяйство и назначена' : 'Вариант техкарты назначен');
        return;
      }
      if (needsFork) {
        const source = cropCards.find((c) => c.id === cardId);
        const suffix = row.predecessor_name ? `по ${row.predecessor_name.toLowerCase()}` : 'без предшественника';
        const copy = await clone.mutateAsync({ cardId, name: `${source?.name ?? 'Вариант'} · ${suffix}` });
        await patchCard.mutateAsync({
          cardId: copy.id,
          patch: { predecessor_crop_id: row.predecessor_crop_id },
        });
        targetCardId = copy.id;
      }
      await assign.mutateAsync({
        scenarioId: variantId,
        techCardId: targetCardId,
        cropId: row.crop_id,
        areaHa: row.area_ha,
        targetYieldCHa: row.yield_c_ha,
      });
      toast.success(needsFork ? 'Копия варианта заведена и назначена строке' : 'Вариант техкарты назначен');
    } catch {
      // Текст отказа уже показан мутацией — второй тост был бы шумом.
    }
  };

  if (!season.validYear || season.missing) {
    return (
      <Shell year={params.year}>
        <SeasonYearOutOfWindowCard year={year} />
      </Shell>
    );
  }
  if (season.isLoading) {
    return (
      <Shell year={params.year}>
        <Skeleton className="h-64 w-full" />
      </Shell>
    );
  }
  if (season.notOpened) {
    return (
      <Shell year={params.year}>
        <SeasonNotStartedCard year={year} onOpened={() => undefined} />
      </Shell>
    );
  }
  if (!canRead) {
    return (
      <Shell year={params.year}>
        <Alert>
          <AlertDescription>У вас нет доступа к техкартам и вариантам плана сезона.</AlertDescription>
        </Alert>
      </Shell>
    );
  }

  const coverage = summary.data?.coverage;
  const totals = summary.data?.totals;
  const calc = summary.data?.calculation;
  const otherVariants = (variants.data ?? []).filter((v) => v.id !== variantId);

  return (
    <Shell year={params.year} title={summary.data ? `Вариант «${summary.data.scenario.name}» · назначение техкарт` : undefined}>
      <FarmScopeNotice scope={farmScope} />

      {farmId && variants.isLoading && <Skeleton className="h-10 w-full" />}

      {/* Ошибка ≠ пустота: на ошибке НЕ зовём «создать вариант» — действие
          упадёт так же, как упало чтение. */}
      {farmId && variants.isError && (
        <Alert variant="destructive">
          <AlertDescription>{describeApiError(variants.error)}</AlertDescription>
        </Alert>
      )}

      {farmId && !variants.isLoading && !variants.isError && (variants.data ?? []).length === 0 && (
        <Alert>
          <AlertDescription>
            <p>
              У этого хозяйства ещё нет ни одного варианта плана на сезон {year}. Вариант плана —
              это структура посевов плюс назначенные техкарты; с него начинаются посевной план,
              структура посевных площадей и сводка.
            </p>
            <div className="mt-3">
              <CreateVariantButton
                emptyState
                farmId={farmId}
                seasonId={season.seasonId}
                seasonYear={year}
                farmName={farmName}
                existingNames={[]}
                onCreated={(id) => setSearch({ variant: id, row: undefined })}
              />
            </div>
          </AlertDescription>
        </Alert>
      )}

      {farmId && (variants.data ?? []).length > 0 && (
        <VariantTabs
          variants={variants.data ?? []}
          currentId={variantId}
          onSelect={(id) => setSearch({ variant: id, row: undefined })}
          actions={
            <>
              {/* Вход в по-полевой посевной план того же варианта: здесь
                  назначают технологию, а «что где сеем» правят там. Раньше
                  добраться до распределения по полям было неоткуда. */}
              {variantId && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    to="/planning/seasons/$year/sowing-plan"
                    params={{ year: String(year) }}
                    search={{ variant: variantId }}
                  >
                    Посевной план по полям
                  </Link>
                </Button>
              )}
              {/* Вход в сравнение и утверждение (AK-772): решение о бюджете
                  принимают, глядя на варианты рядом, — экран сравнения
                  откроется на текущем варианте. */}
              {variantId && (
                <Button asChild size="sm" variant="outline">
                  <Link
                    to="/planning/seasons/$year/programs/compare"
                    params={{ year: String(year) }}
                    search={{ variant: variantId }}
                  >
                    Сравнить варианты и утвердить
                  </Link>
                </Button>
              )}
              {variantId && (
                <AddCropButton
                  scenarioId={variantId}
                  variantName={summary.data?.scenario.name ?? 'вариант'}
                  seasonYear={year}
                />
              )}
              <CreateVariantButton
                farmId={farmId}
                seasonId={season.seasonId}
                seasonYear={year}
                farmName={farmName}
                existingNames={(variants.data ?? []).map((v) => v.name)}
                onCreated={(id) => setSearch({ variant: id, row: undefined })}
              />
            </>
          }
        />
      )}

      {variantId && summary.isLoading && <Skeleton className="h-96 w-full" />}

      {variantId && summary.isError && (
        <Alert variant="destructive">
          <AlertDescription>Не удалось посчитать вариант. Обновите страницу.</AlertDescription>
        </Alert>
      )}

      {summary.data && coverage && totals && (
        <PageWithRail
          rail={
            <>
              <Card>
                <CardContent className="p-4">
                  <h2 className="text-sm font-semibold text-foreground">Итог по варианту</h2>
                  <div className="mt-2 text-2xl font-semibold tabular-nums text-foreground">
                    {formatRubPerHa(totals.cost_per_ha)}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    средневзвешенные прямые затраты по назначенным техкартам
                  </p>
                  <dl className="mt-3">
                    <RailRow label="Затраты по назначенному" value={formatMln(totals.cost)} />
                    <RailRow label="Площадь в расчёте" value={formatHa(totals.area_ha)} />
                    <RailRow label="Плановая валовка" value={formatTons(totals.gross_t)} />
                    {totals.prices_visible && (
                      <>
                        <RailRow label="Плановая выручка" value={formatMln(totals.revenue)} />
                        <RailRow label="Маржа по назначенному" value={formatMln(totals.margin)} strong />
                      </>
                    )}
                  </dl>
                  {!totals.prices_visible && (
                    <p className="mt-3 rounded-lg border border-border bg-muted/40 p-3 text-xs leading-5 text-muted-foreground">
                      Выручка и маржа не показаны: они считаются по бюджетным справочникам, а доступа к
                      ним у вас нет. Покрытие и себестоимость — полностью ваши.
                    </p>
                  )}
                  {totals.not_in_calc.area_ha > 0 && (
                    <p className="mt-3 rounded-lg border border-border bg-muted/40 p-3 text-xs leading-5 text-muted-foreground">
                      Не в расчёте: {formatHa(totals.not_in_calc.area_ha)} — из них{' '}
                      {formatHa(totals.not_in_calc.unassigned_ha)} без техкарты,{' '}
                      {formatHa(totals.not_in_calc.no_crop_ha)} без культуры,{' '}
                      {formatHa(totals.not_in_calc.fallow_ha)} пар. Эти гектары не превращаются в ноль и
                      не размазываются по остальным культурам.
                      {totals.not_in_calc.reasons.map((reason) => (
                        <span key={reason.reason} className="mt-1 block text-accent-brand">
                          {formatHa(reason.area_ha)} с техкартой, но без выручки — {reason.reason}.
                        </span>
                      ))}
                    </p>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3"
                    onClick={() =>
                      navigate({
                        to: '/planning/seasons/$year/plan-variant/summary',
                        params: { year: String(year) },
                        search: { variant: variantId ?? undefined },
                      })
                    }
                  >
                    Открыть сводку варианта →
                  </Button>
                  {/* Этап 4 (UC-4, Р33) — необязательный: «успеем ли в
                      агросроки тем, что есть». Вход отсюда, потому что
                      проверка имеет смысл только когда карты уже назначены. */}
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-2"
                    onClick={() =>
                      navigate({
                        to: '/planning/seasons/$year/plan-variant/feasibility',
                        params: { year: String(year) },
                        search: { variant: variantId ?? undefined },
                      })
                    }
                  >
                    Проверить выполнимость →
                  </Button>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h2 className="text-sm font-semibold text-foreground">Структура посевов</h2>
                  <dl className="mt-2">
                    {groupRowsByCrop(rows, coverage.arable_ha).map((group) => (
                      <RailRow
                        key={group.title}
                        label={group.title}
                        value={`${formatHa(group.area_ha)}${group.share_pct !== null ? ` · ${Math.round(group.share_pct)} %` : ''}`}
                      />
                    ))}
                    <RailRow label="Всего пашни" value={formatHa(coverage.arable_ha)} strong />
                  </dl>
                  <p className="mt-2 text-xs leading-5 text-muted-foreground">
                    Структура меняется кнопкой «Культура в план» наверху: культура + поля, на
                    которых она будет расти.
                  </p>
                </CardContent>
              </Card>
            </>
          }
        >
          <CoverageCard coverage={coverage} rows={rows} />

          {rows.length === 0 && (
            <Card>
              <CardContent className="p-4">
                <h2 className="text-sm font-semibold text-foreground">
                  Вариант «{summary.data.scenario.name}» · культур в плане нет
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Пока не сказано, что и где сеем, считать нечего: структура пустая, покрытие
                  техкартами тоже. Заведите первую культуру — выберите её и поля, на которых
                  она будет расти. Площадь возьмётся с контуров полей, предшественник — с
                  факта прошлого сезона.
                </p>
                <div className="mt-3">
                  {variantId && (
                    <AddCropButton
                      emptyState
                      scenarioId={variantId}
                      variantName={summary.data.scenario.name}
                      seasonYear={year}
                    />
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {rows.length > 0 && coverage.rows_assigned === 0 && (
            <Card>
              <CardContent className="p-4">
                <h2 className="text-sm font-semibold text-foreground">
                  Вариант «{summary.data.scenario.name}» · техкарты не назначены
                </h2>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  Структура посевов есть, технологии — нет. Экономики нет ни по одной культуре, в
                  сравнении вариантов такой вариант помечен «не посчитан».
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {otherVariants.map((other) => (
                    <Button
                      key={other.id}
                      size="sm"
                      disabled={!canWrite || busy}
                      onClick={() => copyAssignments.mutate({ targetId: variantId as string, sourceId: other.id })}
                    >
                      Скопировать назначения из «{other.name}»
                    </Button>
                  ))}
                </div>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">
                  Копирование назначений — самый быстрый способ собрать второй вариант: берём
                  назначения соседнего и меняем одну-две строки.
                </p>
              </CardContent>
            </Card>
          )}

          {selectedRow && (selectedRow.state === 'assigned' || selectedRow.state === 'unassigned') && (
            <AssignPanel
              row={selectedRow}
              cards={cropCards}
              cardsLoading={cards.isLoading}
              canWrite={canWrite && canCreate}
              busy={busy}
              predecessorNames={predecessorNames}
              onAssign={(action) => void handleAssign(action)}
              onUnassign={() =>
                selectedRow.tech_card &&
                variantId &&
                unassign.mutate({ scenarioId: variantId, techCardId: selectedRow.tech_card.id })
              }
              onOpenTechCards={() =>
                navigate({
                  to: '/planning/seasons/$year/tech-cards',
                  params: { year: String(year) },
                  search: {},
                })
              }
            />
          )}

          {/* Обратный ход к заведению культуры: иначе ошибочная строка снимается
              только обходом карточек полей по одной. */}
          {selectedRow?.crop_id && variantId && (
            <div className="flex justify-end">
              <RemoveCropButton
                scenarioId={variantId}
                cropId={selectedRow.crop_id}
                cropName={selectedRow.crop_name ?? 'культура'}
                predecessorCropId={selectedRow.predecessor_crop_id}
                predecessorName={selectedRow.predecessor_name}
                fieldsCount={selectedRow.fields_count}
              />
            </div>
          )}

          {calc && (
            <Card>
              <CardContent className="flex flex-wrap items-center gap-2 p-4 text-sm">
                <span className="text-muted-foreground">Считаем по бюджетным справочникам:</span>
                {calc.catalogs.map((c) => (
                  <Badge key={c.catalog_kind} variant={c.status === 'revised' ? 'accent-brand' : 'outline'}>
                    {c.title} v{c.version}
                  </Badge>
                ))}
                <span className="text-xs text-muted-foreground">
                  расчёт варианта от {formatDate(calc.calculated_at)}
                </span>
                {calc.is_stale && <Badge variant="accent-brand">справочники ушли вперёд</Badge>}
              </CardContent>
            </Card>
          )}

          <PlanRowsTable
            rows={rows}
            arableHa={coverage.arable_ha}
            selectedKey={selectedRow?.key ?? null}
            onSelect={(row: PlanRow) => setSearch({ row: row.key })}
          />
        </PageWithRail>
      )}
    </Shell>
  );
}

function RailRow({ label, value, strong = false }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={
        strong
          ? 'flex min-h-10 items-center border-t border-border py-2 text-sm font-semibold leading-6'
          : 'flex min-h-10 items-center border-b border-border py-2 text-sm leading-6'
      }
    >
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="ml-auto tabular-nums text-foreground">{value}</dd>
    </div>
  );
}

function Shell({ year, title, children }: { year?: string; title?: string; children: React.ReactNode }) {
  return (
    // AK-866 В-3: область — из переключателя. Своего выбора хозяйства на
    // экране нет; `POST /scope/set` отсюда зовётся только deep-link'ом
    // `?farm=` и только через `useScopeSetter` (AK-897).
    <AppShell>
      <PageHeader
        title={title ?? 'Вариант посевного плана · назначение техкарт'}
        description="На каждую пару «культура + предшественник» назначается вариант техкарты. Пока вариант не назначен — экономика по этой площади не считается."
        breadcrumbs={[
          { label: 'Планирование сезона', to: '/planning' },
          // Средняя крошка ведёт в хаб бюджета сезона (макет v2-00).
          {
            label: `Бюджет сезона ${year ?? ''}`.trim(),
            ...(year ? { to: `/planning/seasons/${year}/budget` } : {}),
          },
          { label: 'Назначение техкарт' },
        ]}
      />
      <div className="mt-4 flex flex-col gap-4">{children}</div>
    </AppShell>
  );
}
